import { useMemo } from 'react'
import type { LucideIcon } from 'lucide-react'
import {
  RefreshCw,
  BookOpen,
  Layers,
  Target,
  Award,
  FileText,
  Activity,
  Users,
  AlertCircle,
  TrendingUp,
  GraduationCap,
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useDashboardKpis } from '@/hooks/useDashboardKpis'
import {
  AdminDashboardKpis,
  InstructorDashboardKpis,
  LearnerDashboardKpis,
  isAdminDashboardKpis,
  isInstructorDashboardKpis,
  isLearnerDashboardKpis,
} from '@/models/dashboard-kpi.model'

interface MetricCardProps {
  title: string
  value: string
  icon: LucideIcon
  subtitle?: string
}

const MetricCard = ({ title, value, icon: Icon, subtitle }: MetricCardProps) => (
  <Card className="bg-white border border-gray-200">
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <CardTitle className="text-sm font-medium text-gray-600">{title}</CardTitle>
      <Icon className="h-5 w-5 text-gray-400" />
    </CardHeader>
    <CardContent>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      {subtitle ? <p className="text-xs text-gray-500">{subtitle}</p> : null}
    </CardContent>
  </Card>
)

const safeNumber = (value: number): number => (Number.isFinite(value) ? value : 0)

const formatNumber = (value: number): string => safeNumber(value).toLocaleString()

const clampPercentage = (value: number): number => {
  if (!Number.isFinite(value)) return 0
  return Math.min(100, Math.max(0, Math.round(value)))
}

const formatPercentage = (value: number): string => `${clampPercentage(value)}%`

const formatDate = (value: string | null): string => {
  if (!value) return 'N/A'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleDateString()
}

const formatDateTime = (value: string | null): string => {
  if (!value) return 'N/A'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleString()
}

const capitalize = (value: string): string => {
  if (!value) return value
  return value.charAt(0).toUpperCase() + value.slice(1)
}

const getStatusBadgeClass = (status: string): string => {
  const normalized = status.toLowerCase()
  if (normalized.includes('completed') || normalized.includes('graded')) {
    return 'border-transparent bg-green-100 text-green-700'
  }
  if (normalized.includes('pending') || normalized.includes('draft')) {
    return 'border-transparent bg-yellow-100 text-yellow-700'
  }
  if (normalized.includes('overdue') || normalized.includes('failed')) {
    return 'border-transparent bg-red-100 text-red-700'
  }
  return 'border-transparent bg-gray-100 text-gray-700'
}

const renderLearnerSection = (kpis: LearnerDashboardKpis) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      <MetricCard
        title="Enrolled Courses"
        value={formatNumber(kpis.totalCourses)}
        icon={BookOpen}
        subtitle="Active registrations"
      />
      <MetricCard
        title="Modules Completed"
        value={`${formatNumber(kpis.modulesCompleted)} / ${formatNumber(kpis.modulesTotal)}`}
        icon={Layers}
        subtitle="Progress across all courses"
      />
      <MetricCard
        title="Overall Progress"
        value={formatPercentage(kpis.percentComplete)}
        icon={Target}
        subtitle="Weighted completion"
      />
      <MetricCard
        title="Certificates Earned"
        value={formatNumber(kpis.certificatesEarned)}
        icon={Award}
        subtitle="Issued certificates"
      />
    </div>

    <Card className="bg-white border border-gray-200">
      <CardHeader>
        <CardTitle className="text-base font-semibold text-gray-900">Learning Progress</CardTitle>
        <CardDescription>
          Last active: <span className="font-medium text-gray-700">{formatDateTime(kpis.lastActiveAt)}</span>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div>
            <div className="mb-2 flex items-center justify-between text-sm text-gray-600">
              <span>Overall completion</span>
              <span className="font-semibold text-gray-900">{formatPercentage(kpis.percentComplete)}</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
              <div
                className="h-full bg-blue-600 transition-all"
                style={{ width: `${clampPercentage(kpis.percentComplete)}%` }}
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-600">Assignments</p>
              <ul className="space-y-1 text-sm text-gray-700">
                <li>
                  Drafts:{' '}
                  <span className="font-semibold text-gray-900">
                    {formatNumber(kpis.assignments.drafts)}
                  </span>
                </li>
                <li>
                  Submitted:{' '}
                  <span className="font-semibold text-gray-900">
                    {formatNumber(kpis.assignments.submitted)}
                  </span>
                </li>
                <li>
                  Graded:{' '}
                  <span className="font-semibold text-gray-900">
                    {formatNumber(kpis.assignments.graded)}
                  </span>
                </li>
              </ul>
            </div>
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-600">Quizzes</p>
              <ul className="space-y-1 text-sm text-gray-700">
                <li>
                  Attempts:{' '}
                  <span className="font-semibold text-gray-900">{formatNumber(kpis.quizzes.attempts)}</span>
                </li>
                <li>
                  Average score:{' '}
                  <span className="font-semibold text-gray-900">
                    {formatPercentage(kpis.quizzes.avgScore)}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>

    <Card className="bg-white border border-gray-200">
      <CardHeader>
        <CardTitle className="text-base font-semibold text-gray-900">Current Enrolments</CardTitle>
        <CardDescription>Total active course registrations</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center">
          <p className="text-sm font-medium text-gray-600">Active enrolments</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">
            {formatNumber(kpis.enrollments.length)}
          </p>
        </div>
      </CardContent>
    </Card>
  </div>
)

const renderInstructorSection = (kpis: InstructorDashboardKpis) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
      <MetricCard
        title="Courses Managed"
        value={formatNumber(kpis.coursesCount)}
        icon={BookOpen}
        subtitle="Total live courses"
      />
      <MetricCard
        title="Active Students"
        value={formatNumber(kpis.activeStudents)}
        icon={Users}
        subtitle="Engaged students"
      />
      <MetricCard
        title="Pending Submissions"
        value={formatNumber(kpis.pendingSubmissions)}
        icon={FileText}
        subtitle="Awaiting review"
      />
      <MetricCard
        title="Average Student Score"
        value={formatPercentage(kpis.avgStudentScorePct)}
        icon={TrendingUp}
        subtitle="Across all assessments"
      />
      <MetricCard
        title="Completion Rate"
        value={formatPercentage(kpis.courseCompletionRate)}
        icon={Target}
        subtitle="Course-wide completion"
      />
      <MetricCard
        title="Content Uploads"
        value={formatNumber(kpis.contentUploads)}
        icon={Activity}
        subtitle="Resources shared"
      />
    </div>

    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <Card className="bg-white border border-gray-200">
        <CardHeader>
          <CardTitle className="text-base font-semibold text-gray-900">Recent Submissions</CardTitle>
          <CardDescription>Latest student submissions awaiting feedback</CardDescription>
        </CardHeader>
        <CardContent>
          {kpis.recentSubmissions.length > 0 ? (
            <div className="space-y-3">
              {kpis.recentSubmissions.slice(0, 6).map((submission) => (
                <div
                  key={submission.submissionId}
                  className="rounded-lg border border-gray-200 p-3"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {submission.courseTitle || 'Untitled course'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {submission.learnerName || 'Unknown student'}
                      </p>
                    </div>
                    {submission.status ? (
                      <Badge className={getStatusBadgeClass(submission.status)}>
                        {capitalize(submission.status)}
                      </Badge>
                    ) : null}
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    Submitted: {formatDateTime(submission.submittedAt ?? null)}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No recent submissions.</p>
          )}
        </CardContent>
      </Card>

      <Card className="bg-white border border-gray-200">
        <CardHeader>
          <CardTitle className="text-base font-semibold text-gray-900">Recent Quiz Attempts</CardTitle>
          <CardDescription>Latest quiz activity from your students</CardDescription>
        </CardHeader>
        <CardContent>
          {kpis.recentQuizAttempts.length > 0 ? (
            <div className="space-y-3">
              {kpis.recentQuizAttempts.slice(0, 6).map((attempt) => (
                <div
                  key={attempt.attemptId}
                  className="rounded-lg border border-gray-200 p-3"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {attempt.quizTitle || 'Untitled quiz'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {attempt.learnerName || 'Unknown student'}
                      </p>
                    </div>
                    {Number.isFinite(attempt.score ?? null) ? (
                      <Badge
                        variant="secondary"
                        className="border-transparent bg-indigo-100 text-indigo-700"
                      >
                        Score: {formatPercentage(attempt.score ?? 0)}
                      </Badge>
                    ) : null}
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    Attempted: {formatDateTime(attempt.attemptedAt ?? null)}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No recent quiz attempts.</p>
          )}
        </CardContent>
      </Card>
    </div>

    <Card className="bg-white border border-gray-200">
      <CardHeader>
        <CardTitle className="text-base font-semibold text-gray-900">Courses Overview</CardTitle>
        <CardDescription>Snapshot of each managed course</CardDescription>
      </CardHeader>
      <CardContent>
        {kpis.courses.length > 0 ? (
          <div className="space-y-3">
            {kpis.courses.slice(0, 8).map((course) => (
              <div
                key={course.courseId || course.courseTitle}
                className="flex flex-col gap-2 rounded-lg border border-gray-200 p-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="space-y-1">
                  <p className="text-sm font-medium text-gray-900">{course.courseTitle || 'Untitled course'}</p>
                  <p className="text-xs text-gray-500">
                    {course.courseCode ? `${course.courseCode} • ` : ''}
                    Last updated {formatDateTime(course.updatedAt ?? null)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge
                    variant="secondary"
                    className="border-transparent bg-blue-100 text-blue-700"
                  >
                    Students: {formatNumber(course.enrollmentCount ?? 0)}
                  </Badge>
                  <Badge
                    variant="secondary"
                    className="border-transparent bg-green-100 text-green-700"
                  >
                    Completion: {formatPercentage(course.completionRate ?? 0)}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No courses found.</p>
        )}
      </CardContent>
    </Card>
  </div>
)

const renderAdminSection = (kpis: AdminDashboardKpis) => {
  const usersByRoleEntries = Object.entries(kpis.usersByRole ?? {})
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        <MetricCard
          title="Total Courses"
          value={formatNumber(kpis.totalCourses)}
          icon={BookOpen}
          subtitle="Courses in the catalogue"
        />
        <MetricCard
          title="Active Courses"
          value={formatNumber(kpis.activeCourses)}
          icon={Activity}
          subtitle="Currently available to students"
        />
        <MetricCard
          title="Active Students"
          value={formatNumber(kpis.activeLearners)}
          icon={GraduationCap}
          subtitle="Logged in within monitoring window"
        />
        <MetricCard
          title="Total Programmes"
          value={formatNumber(kpis.totalProgrammes)}
          icon={Layers}
          subtitle="Programmes offered"
        />
        <MetricCard
          title="Applications Pending"
          value={formatNumber(kpis.applicationsPending)}
          icon={FileText}
          subtitle="Awaiting review"
        />
        <MetricCard
          title="Global Completion"
          value={formatPercentage(kpis.globalCourseCompletionRate)}
          icon={Target}
          subtitle="Institution-wide average"
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="bg-white border border-gray-200 xl:col-span-2">
          <CardHeader>
          <CardTitle className="text-base font-semibold text-gray-900">Programme Enrolments</CardTitle>
          <CardDescription>Total enrolments across all programmes</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center">
            <p className="text-sm font-medium text-gray-600">Total programme enrolments</p>
            <p className="mt-2 text-3xl font-bold text-gray-900">
              {formatNumber(
                kpis.enrollmentsByProgramme.reduce((acc, programme) => acc + (programme.enrollments ?? 0), 0),
              )}
            </p>
          </div>
        </CardContent>
      </Card>

        <Card className="bg-white border border-gray-200">
          <CardHeader>
            <CardTitle className="text-base font-semibold text-gray-900">User Distribution</CardTitle>
            <CardDescription>Active users grouped by role</CardDescription>
          </CardHeader>
          <CardContent>
            {usersByRoleEntries.length > 0 ? (
              <ul className="space-y-2 text-sm text-gray-700">
                {usersByRoleEntries.map(([role, count]) => (
                  <li key={role} className="flex items-center justify-between">
                    <span className="capitalize text-gray-600">{role}</span>
                    <span className="font-semibold text-gray-900">{formatNumber(count)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">No user distribution data.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="bg-white border border-gray-200">
          <CardHeader>
            <CardTitle className="text-base font-semibold text-gray-900">Payments Overview</CardTitle>
            <CardDescription>Recent revenue activity</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">
              Total amount:{' '}
              <span className="font-semibold text-gray-900">
                {formatNumber(kpis.payments.totalAmount)}
              </span>
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Transactions:{' '}
              <span className="font-semibold text-gray-900">
                {formatNumber(kpis.payments.count)}
              </span>
            </p>
            <p className="mt-2 text-xs text-gray-500">
              Period: {formatDate(kpis.payments.period.from)} – {formatDate(kpis.payments.period.to)}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-white border border-gray-200">
          <CardHeader>
            <CardTitle className="text-base font-semibold text-gray-900">System Usage</CardTitle>
            <CardDescription>Logins across the platform</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-center justify-between">
                <span>Last 7 days</span>
                <span className="font-semibold text-gray-900">
                  {formatNumber(kpis.systemUsage.logins7)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span>Last 30 days</span>
                <span className="font-semibold text-gray-900">
                  {formatNumber(kpis.systemUsage.logins30)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-gray-200">
          <CardHeader>
            <CardTitle className="text-base font-semibold text-gray-900">Student Snapshot</CardTitle>
            <CardDescription>Quick view of student activity</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center justify-between">
                <span>Active students</span>
                <span className="font-semibold text-gray-900">
                  {formatNumber(kpis.activeLearners)}
                </span>
              </li>
              <li className="flex items-center justify-between">
                <span>Active courses</span>
                <span className="font-semibold text-gray-900">
                  {formatNumber(kpis.activeCourses)}
                </span>
              </li>
              <li className="flex items-center justify-between">
                <span>Global completion</span>
                <span className="font-semibold text-gray-900">
                  {formatPercentage(kpis.globalCourseCompletionRate)}
                </span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export function DashboardPage() {
  const { user } = useAuth()
  const { role, data, loading, error, refreshing, refresh } = useDashboardKpis()

  const greeting = useMemo(() => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good morning'
    if (hour < 17) return 'Good afternoon'
    return 'Good evening'
  }, [])

  const handleRefresh = async () => {
    try {
      await refresh()
    } catch {
      // Error state is already managed by the hook
    }
  }

  const renderDashboardContent = () => {
    if (loading) {
      return (
        <Card className="bg-white border border-gray-200">
          <CardContent className="py-10 text-center text-sm text-gray-500">
            Loading dashboard data...
          </CardContent>
        </Card>
      )
    }

    if (error) {
      return (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <div>
            <p className="font-semibold">Unable to load dashboard data</p>
            <p className="mt-1 text-red-600">{error}</p>
          </div>
        </div>
      )
    }

    if (!role || !data) {
      return (
        <Card className="bg-white border border-gray-200">
          <CardContent className="py-10 text-center text-sm text-gray-500">
            Sign in to view your dashboard insights.
          </CardContent>
        </Card>
      )
    }

    if (isLearnerDashboardKpis(data)) {
      return renderLearnerSection(data)
    }

    if (isInstructorDashboardKpis(data)) {
      return renderInstructorSection(data)
    }

    if (isAdminDashboardKpis(data)) {
      return renderAdminSection(data)
    }

    return (
      <Card className="bg-white border border-gray-200">
        <CardContent className="py-10 text-center text-sm text-gray-500">
          Dashboard insights are not available for this role.
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="border-b border-gray-200 bg-white px-4 py-4 sm:px-6">
        <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <div>
            <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Dashboard</h1>
            <p className="text-sm text-gray-600 sm:text-base">
              {greeting}, {user?.firstName || 'User'}!
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing || loading}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="space-y-4 p-4 sm:space-y-6 sm:p-6">
        {renderDashboardContent()}
      </div>
    </div>
  )
}
